import { ElementRef, inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';
import { ENVIRONMENT } from '@env/environment';
import { Router } from '@angular/router';
import { getEnabledPages } from '@shared/utils/environment-utils';
import { SystemService } from '@shared/service/system/system.service';
import { AngularWindow } from '../model/interfaces/angular-window.interface';
import { Observable } from 'rxjs';
import { Project } from '@shared/model/interfaces/project.interface';
import { Company } from '@shared/model/interfaces/company.interface';
import { NavigationTypes } from '@shared/model/enums/navigation-types.enum';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationStateManager, NavigationPhase } from './navigation-state-manager.service';
import { ToasterBridgeService } from './toaster-bridge.service';

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private systemService = inject(SystemService);
  private navStateManager = inject(NavigationStateManager);
  private toasterBridge = inject(ToasterBridgeService);

  private _isVisible = false;
  public isNavigationEvent = NavigationTypes.PENDING;
  private _showOldApp = true;
  private oldAppIframe!: ElementRef;
  private iframeUrlSubject = new BehaviorSubject<SafeResourceUrl | null>(null);
  iframeUrl$ = this.iframeUrlSubject.asObservable();
  private visibilitySubject = new BehaviorSubject<boolean>(true);
  visibilityChanged$: Observable<boolean> = this.visibilitySubject.asObservable();

  // Debug logs
  private readonly LOG_PREFIX = '[LayoutService]';

  private injector = inject(Injector);

  constructor() {
    // Note: Style application on navigation is now handled by the iframe 'load' event
    // listener in setIframe(). This is more reliable than popstate/pageshow handlers
    // because it fires whenever the iframe content changes, regardless of how the
    // navigation was triggered.
  }

  /**
   * Immediately hides the iframe via direct DOM manipulation.
   * This is faster than Angular's change detection and prevents flash of content.
   */
  private hideIframeImmediately(): void {
    try {
      const iframe = document.getElementById('oldAppIframe') as HTMLIFrameElement | null;
      if (iframe) {
        // Set opacity to 0 immediately - matches .iframe-hidden class
        iframe.style.opacity = '0';
        // Also remove the visible class and add hidden class for consistency
        iframe.classList.remove('iframe-visible');
        iframe.classList.add('iframe-hidden');
      }
    } catch (e) {
      console.error('[LayoutService] Error hiding iframe:', e);
    }
  }

  /**
   * Shows the iframe after styles have been applied.
   * Clears any inline styles set by hideIframeImmediately and updates Angular binding.
   */
  private showIframeAfterStyles(): void {
    try {
      const iframe = document.getElementById('oldAppIframe') as HTMLIFrameElement | null;
      if (iframe) {
        // Clear inline opacity style so CSS classes take over
        iframe.style.opacity = '';
        // Update classes for CSS transition
        iframe.classList.remove('iframe-hidden');
        iframe.classList.add('iframe-visible');
      }
    } catch (e) {
      console.error('[LayoutService] Error showing iframe:', e);
    }
    // Update Angular binding
    this.isVisible = true;
  }

  set showOldApp(value: boolean) {
    this._showOldApp = value;
  }

  get showOldApp(): boolean {
    return this._showOldApp;
  }

  setIframe(iframe: ElementRef) {
    this.oldAppIframe = iframe;

    // CRITICAL: Add load event listener to ensure styles are applied whenever iframe content loads
    // This catches: normal navigation, bfcache restoration, and any other content changes
    if (iframe?.nativeElement) {
      iframe.nativeElement.addEventListener('load', () => {
        // CRITICAL: Only process iframe load if we're showing the old app (iframe pages)
        // When on native Angular pages (showOldApp=false), the iframe might still fire load events
        // but we should NOT apply styles or update NavStateManager, as this would interfere
        // with future navigation back to iframe pages
        if (!this._showOldApp) {
          return;
        }

        // Hide iframe immediately, then apply styles
        this.hideIframeImmediately();
        this.isVisible = false;
        // Apply styles after a short delay to ensure content is ready
        setTimeout(() => {
          this.applyStyles();
        }, 50);
      });
    }
  }

  get isVisible(): boolean {
    return this._isVisible;
  }

  set isVisible(value: boolean) {
    if (this._isVisible !== value) {
      this._isVisible = value;
      this.visibilitySubject.next(value);
    }
  }

  // TODO: DELETE WHEN REFACTOR DONE ----------------

  private _navigationInitiatedByTopWindow: string | null = null;

  lastSyncedUrl: string | null = null;

  get navigationInitiatedByTopWindowRoute(): string | null {
    return this._navigationInitiatedByTopWindow;
  }

  set navigationInitiatedByTopWindow(route: string | null) {
    this._navigationInitiatedByTopWindow = route;
  }

  get navigationInitiatedByTopWindow(): boolean {
    return this._navigationInitiatedByTopWindow !== null;
  }

  // 40 * 250ms = 10 seconds max wait time
  applyStyles(retryCount = 40, delay = 250): Promise<boolean> {
    return new Promise((resolve) => {
      // CRITICAL: Only set APPLYING_STYLES if not already READY
      // When using location.go(), navigation completes before applyStyles is called
      // Don't regress from READY back to APPLYING_STYLES
      const currentPhase = this.navStateManager.currentState.phase;
      if (currentPhase !== NavigationPhase.READY && currentPhase !== NavigationPhase.ERROR) {
        this.navStateManager.updatePhase(NavigationPhase.APPLYING_STYLES);
      }

      try {
        const iframeContentWindow = this.oldAppIframe?.nativeElement?.contentWindow;
        const iframeDoc = iframeContentWindow?.document;

        const [html, navigationBar, sideBar, pageContent, mainContainer] = [
          'html',
          '.navbar',
          '.page-sidebar',
          'div.page-content',
          '.main-container',
        ].map((selector) => iframeDoc?.querySelector(selector));

        // Retry if any critical element is missing
        if (!iframeDoc || !html || !navigationBar || !sideBar || !mainContainer) {
          if (retryCount > 0) {
            setTimeout(async () => {
              const success = await this.applyStyles(retryCount - 1, delay);
              resolve(success);
            }, delay);
          } else {
            this.error('applyStyles: Max retries reached. Required elements not found.');

            // CRITICAL: Even if elements not found, try to hide sidebar/navbar using fallback selectors
            // This prevents the "duplicate sidebar" issue when styles fail to apply
            this.warn('Attempting fallback style application...');
            try {
              // Try broader selectors to hide navigation elements
              const allNavbars = iframeDoc?.querySelectorAll('.navbar, nav, [class*="navbar"]');
              allNavbars?.forEach((el: Element) => {
                (el as HTMLElement).style.display = 'none';
              });

              const allSidebars = iframeDoc?.querySelectorAll('.page-sidebar, .sidebar, aside, [class*="sidebar"]');
              allSidebars?.forEach((el: Element) => {
                (el as HTMLElement).style.display = 'none';
              });

              this.warn('Fallback styles applied - hiding navbar/sidebar elements');
            } catch (fallbackError) {
              this.error('Fallback style application also failed:', fallbackError);
            }

            this.navStateManager.markStylesApplied(false);
            this.isVisible = true; // Show anyway to prevent indefinite loading
            resolve(false);
          }
          return;
        }

        // CRITICAL FIX: Inject CSS rules for sticky elements BEFORE they exist
        // This solves the timing issue where #sticky/#sticky-bar are created by ocLazyLoad
        // AFTER applyStyles runs. CSS rules will automatically apply when elements are created.
        this.injectStickyStylesheet(iframeDoc);

        // Apply inline styles for elements that exist NOW
        // (sticky elements will get their styles from the injected CSS)
        (navigationBar as HTMLElement).style.display = 'none';
        (mainContainer as HTMLElement).style.setProperty('margin-top', '0', 'important');
        (sideBar as HTMLElement).style.display = 'none';
        (pageContent as HTMLElement)?.style.setProperty('margin-left', '0', 'important');
        (html as HTMLElement).style.minWidth = '100%';

        // CRITICAL: Initialize toaster bridge to intercept iframe toasters
        // and display them in parent window where they're always visible
        const iframeWindow = this.oldAppIframe?.nativeElement?.contentWindow;
        if (iframeWindow) {
          this.toasterBridge.initializeBridge(iframeWindow);
          // Pass enabled pages to only show parent toasts for migrated pages
          this.toasterBridge.injectToasterInterceptor(iframeDoc!, getEnabledPages());
        }

        // Show iframe properly (clears any inline styles set by hideIframeImmediately)
        this.showIframeAfterStyles();
        this.navStateManager.markStylesApplied(true);
        resolve(true);
      } catch (error) {
        if (retryCount > 0) {
          setTimeout(async () => {
            const success = await this.applyStyles(retryCount - 1, delay);
            resolve(success);
          }, delay);
        } else {
          console.log('applyStyles: Max retries reached with error:', error);
          this.navStateManager.markStylesApplied(false);
          // Show iframe anyway (clears any inline styles)
          this.showIframeAfterStyles();
          resolve(false);
        }
      }
    });
  }

  // Store observers for cleanup when navigating away
  private stickyMutationObserver: MutationObserver | null = null;
  private stickyResizeObserver: ResizeObserver | null = null;
  private stickyCheckInterval: ReturnType<typeof setInterval> | null = null;

  /**
   * Cleans up all sticky-related observers and intervals.
   * Called before setting up new observers to prevent memory leaks and errors
   * when navigating away from template pages.
   */
  private cleanupStickyObservers(): void {
    if (this.stickyMutationObserver) {
      this.stickyMutationObserver.disconnect();
      this.stickyMutationObserver = null;
    }
    if (this.stickyResizeObserver) {
      this.stickyResizeObserver.disconnect();
      this.stickyResizeObserver = null;
    }
    if (this.stickyCheckInterval) {
      clearInterval(this.stickyCheckInterval);
      this.stickyCheckInterval = null;
    }
  }

  /**
   * Injects a stylesheet into the iframe document to handle sticky header positioning.
   * This approach solves the timing issue where #sticky and #sticky-bar elements are
   * created by ocLazyLoad AFTER applyStyles runs. CSS rules automatically apply when
   * elements are created - no polling or MutationObserver needed.
   *
   * Why this works:
   * - Old app CSS has: #sticky { position: fixed; top: 50px; } (50px for navbar)
   * - We hide the navbar, so top should be 0, not 50px
   * - By injecting CSS with higher specificity, we override the old app's styles
   * - The rules apply automatically whenever the elements are created by ocLazyLoad
   */
  private injectStickyStylesheet(iframeDoc: Document): void {
    // CRITICAL: Clean up any existing observers from previous page
    // This prevents errors when navigating away from template pages
    this.cleanupStickyObservers();

    const styleId = 'manager-studio-sticky-fix';

    // Only inject CSS if not already present
    if (!iframeDoc.getElementById(styleId)) {
      const styleElement = iframeDoc.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        /* CRITICAL: Hide old app's navbar and sidebar via CSS */
        /* This is more reliable than inline styles - persists on bfcache restoration */
        .navbar,
        nav.navbar,
        .navbar-fixed-top {
          display: none !important;
        }

        .page-sidebar,
        aside.page-sidebar {
          display: none !important;
        }

        /* Adjust page content to fill the space left by hidden sidebar */
        .page-content,
        div.page-content {
          margin-left: 0 !important;
        }

        /* Adjust main container to remove navbar offset */
        .main-container {
          margin-top: 0 !important;
        }

        /* CRITICAL: Override old app's sticky positioning since we hide the navbar */
        /* Using high specificity to ensure these rules win over old app's CSS */

        /* Sticky header for template detail pages */
        /* left: 5px to not overlap with Angular sidebar */
        /* width: calc(100vw - 5px) to extend to right edge of viewport */
        #sticky {
          position: fixed !important;
          top: 0 !important;
          padding-left: 5px !important;
          width: calc(100vw - 5px) !important;
          z-index: 1000 !important;
          background: #fff;
          border: 1px solid #ccc;
        }

        /* Sticky bar (alternative sticky element) */
        #sticky-bar {
          position: fixed !important;
          top: 0 !important;
          padding-left: 5px !important;
          width: calc(100vw - 5px) !important;
          z-index: 1000 !important;
          background: #fff;
          border: 1px solid #ccc;
        }

        /* Main content needs margin-top to avoid being hidden under sticky header */
        /* Apply to all .main elements - defaults to 10px when no sticky exists (0px + 10px) */
        /* When sticky exists and is measured: updates to sticky-height + 10px */
        .page-content .main {
          margin-top: 10px !important;
        }

        /* Right sidebar (stickselection) - toolbar panel on template detail pages */
        /* Has position: fixed from old app CSS, so needs explicit top positioning */
        /* Old app: top: 112px (44.5px navbar + ~67.5px sticky) */
        /* New app: top: sticky-height + 10px gap (no navbar to account for) */
        .stickselection {
          top: calc(var(--sticky-header-height, 0px) + 10px) !important;
        }

        /* When ui-scrollfix class is applied (old app's scroll behavior), maintain same positioning */
        .stickselection.ui-scrollfix {
          top: calc(var(--sticky-header-height, 0px) + 10px) !important;
        }
      `;

      iframeDoc.head.appendChild(styleElement);
    }

    // CRITICAL: Always set up observers, even if stylesheet already exists
    // This ensures sticky height is measured on EVERY page navigation
    // Set up a MutationObserver to measure sticky height once it appears and update CSS variable
    this.setupStickyHeightObserver(iframeDoc);
  }

  /**
   * Sets up a MutationObserver to detect when #sticky or #sticky-bar is added to the DOM,
   * then measures its height and updates the CSS custom property.
   */
  private setupStickyHeightObserver(iframeDoc: Document): void {
    let hasSetInitialHeight = false;

    // Check if already exists
    const updateStickyHeight = (): boolean => {
      try {
        // Guard against accessing destroyed iframe
        if (!iframeDoc || !iframeDoc.documentElement) {
          this.cleanupStickyObservers();
          return true; // Return true to stop further attempts
        }

        const sticky = iframeDoc.querySelector('#sticky') || iframeDoc.querySelector('#sticky-bar');
        if (sticky) {
          const height = (sticky as HTMLElement).offsetHeight;
          if (height > 0) {
            // Set exact sticky height - the 10px gap is added via CSS calc()
            iframeDoc.documentElement.style.setProperty('--sticky-header-height', `${height}px`);

            // CRITICAL: Also set inline style on .stickselection to override old app's CSS
            // The old app's template.css loads AFTER our injected CSS and overrides our rules
            // Inline styles have higher specificity and will win
            const stickselection = iframeDoc.querySelector('.stickselection');
            if (stickselection) {
              (stickselection as HTMLElement).style.setProperty('top', `${height + 10}px`, 'important');
            }

            // CRITICAL: Reset scroll position on FIRST height measurement only
            // This prevents the "scrolled down" appearance when navigating to template pages
            if (!hasSetInitialHeight) {
              hasSetInitialHeight = true;
              try {
                // Reset scroll to top of content area
                const mainElement = iframeDoc.querySelector('.main');
                if (mainElement) {
                  mainElement.scrollTop = 0;
                }
                // Also reset the document scroll
                iframeDoc.documentElement.scrollTop = 0;
                if (iframeDoc.defaultView) {
                  iframeDoc.defaultView.scrollTo(0, 0);
                }
              } catch {
                // Ignore scroll errors
              }
            }
            return true;
          }
        }
        return false;
      } catch {
        // Iframe was destroyed, clean up
        this.cleanupStickyObservers();
        return true;
      }
    };

    // Try immediately
    if (updateStickyHeight()) {
      return;
    }

    // Watch for DOM changes to detect when sticky is added
    this.stickyMutationObserver = new MutationObserver(() => {
      if (updateStickyHeight()) {
        this.cleanupStickyObservers();
      }
    });

    try {
      this.stickyMutationObserver.observe(iframeDoc.body, {
        childList: true,
        subtree: true,
      });
    } catch {
      // Iframe body not available
      this.cleanupStickyObservers();
      return;
    }

    // Also set up a ResizeObserver once element exists to handle dynamic height changes
    const setupResizeObserver = (): boolean => {
      try {
        // Guard against accessing destroyed iframe
        if (!iframeDoc || !iframeDoc.documentElement) {
          this.cleanupStickyObservers();
          return true;
        }

        const sticky = iframeDoc.querySelector('#sticky') || iframeDoc.querySelector('#sticky-bar');
        if (sticky) {
          this.stickyResizeObserver = new ResizeObserver(() => {
            try {
              if (!iframeDoc || !iframeDoc.documentElement) {
                this.cleanupStickyObservers();
                return;
              }
              const height = (sticky as HTMLElement).offsetHeight;
              if (height > 0) {
                // Set exact height - the 10px gap is added via CSS calc()
                iframeDoc.documentElement.style.setProperty('--sticky-header-height', `${height}px`);
              }
            } catch {
              this.cleanupStickyObservers();
            }
          });
          this.stickyResizeObserver.observe(sticky);

          // Stop mutation observer since resize observer is now active
          if (this.stickyMutationObserver) {
            this.stickyMutationObserver.disconnect();
            this.stickyMutationObserver = null;
          }
          // Clear the interval since we found the element
          if (this.stickyCheckInterval) {
            clearInterval(this.stickyCheckInterval);
            this.stickyCheckInterval = null;
          }
          return true;
        }
        return false;
      } catch {
        this.cleanupStickyObservers();
        return true;
      }
    };

    // Check periodically for a short time to set up resize observer
    let attempts = 0;
    this.stickyCheckInterval = setInterval(() => {
      if (setupResizeObserver() || attempts++ > 30) {
        if (this.stickyCheckInterval) {
          clearInterval(this.stickyCheckInterval);
          this.stickyCheckInterval = null;
        }
      }
    }, 200);
  }

  // BIND SELECTION CHANGES TO PROPAGATE TO IFRAME SCOPE

  bindSelectChange() {
    runInInjectionContext(this.injector, () => {
      const systemService = inject(SystemService);

      const entries: {
        observable$: Observable<string | number | null>;
        key: string;
        source: Project[] | Company[];
        parse: (v: string | number) => string | number;
      }[] = [
        {
          observable$: toObservable(systemService._selectedCompany),
          key: 'selectedCompanyName',
          source: systemService.companies(),
          parse: (value) => value as string,
        },
        {
          observable$: toObservable(systemService._selectedProject),
          key: 'selectedProjectName',
          source: systemService.projects(),
          parse: (value) => Number(value),
        },
      ];

      entries.forEach(({ observable$, key, source }) => {
        observable$.subscribe((value) => {
          if (!value) return;
          const label = systemService.getLabelById(source, String(value)) || 'All Organizations';
          this.propagateSelectChange(label, key);
        });
      });

      // CRITICAL: Also propagate full project object to iframe for language/resources detection
      // The old AngularJS app watches $rootScope.selectedProject and needs the full object
      // with direction/resources fields to set the correct language
      toObservable(systemService._selectedProject).subscribe((projectId) => {
        if (!projectId) return;
        const fullProject = systemService.projects().find((p) => p.id === projectId);
        if (fullProject) {
          this.propagateProjectObject(fullProject);
        }
      });
    });
  }

  loadUrl(url: string) {
    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.iframeUrlSubject.next(safeUrl);
  }

  propagateSelectChange(value: string, targetField: string) {
    const iframe = document.getElementById('oldAppIframe') as HTMLIFrameElement | null;
    if (!iframe?.contentWindow) {
      console.warn('Iframe or contentWindow is not available.');
      return;
    }

    const angularWindow = iframe?.contentWindow as AngularWindow;
    if (
      this.systemService.selectChangeTrigger &&
      !(angularWindow.location.pathname + '/').includes(ENVIRONMENT.DEFAULT_VIEW.iframeUrl)
    ) {
      this.systemService.selectChangeTrigger = false;
      if (this.isNavigationEvent == NavigationTypes.PENDING) {
        this.router.navigate([ENVIRONMENT.DEFAULT_VIEW.route]);
      }
    } else {
      try {
        const $rootScope = angularWindow.angular
          .element(iframe!.contentWindow!.document!)
          .injector()
          .get('$rootScope') as Record<string, unknown>;

        if ($rootScope) {
          const target = $rootScope[targetField];
          $rootScope[targetField] =
            target && typeof target === 'object' && 'name' in target ? { ...target, name: value } : value;

          ($rootScope['$apply'] as () => void)();
        } else {
          console.log('AngularJS not found inside iframe.');
        }
      } catch {
        // console.error('Error accessing AngularJS inside iframe:', error);
      } finally {
        this.isNavigationEvent = NavigationTypes.PENDING;
      }
    }
  }

  propagateProjectObject(project: Project) {
    const iframe = document.getElementById('oldAppIframe') as HTMLIFrameElement | null;
    if (!iframe?.contentWindow) {
      return;
    }

    try {
      const angularWindow = iframe.contentWindow as AngularWindow;
      const $rootScope = angularWindow.angular
        ?.element(iframe.contentWindow.document)
        ?.injector()
        ?.get('$rootScope') as Record<string, unknown>;

      if ($rootScope) {
        // CRITICAL: Propagate full project object with ALL properties from API
        // The old AngularJS app expects properties like has_project_bpm_show, etc.
        // We must spread the entire project object to avoid "Cannot read properties of undefined" errors
        const existingProject = ($rootScope['selectedProject'] as Record<string, unknown>) || {};
        $rootScope['selectedProject'] = {
          ...existingProject,
          ...project, // Spread ALL properties from the project object
          direction: project.direction || 'rtl',
          resources: project.direction === 'ltr' ? 'english' : 'hebrew',
        };

        ($rootScope['$apply'] as () => void)();
      }
    } catch (_error) {
      // Iframe AngularJS not ready yet, will retry on next project change
    }
  }

  // TODO: DELETE WHEN MIGRATION DONE ^^^^^^^^^^^^^

  // Debug logs
  // Logging methods with console grouping for better debugging
  private log(message: string, ...args: unknown[]): void {
    console.log(`${this.LOG_PREFIX} ${message}`, ...args);
  }

  private warn(message: string, ...args: unknown[]): void {
    console.warn(`${this.LOG_PREFIX} ⚠️ ${message}`, ...args);
  }

  private error(message: string, ...args: unknown[]): void {
    console.error(`${this.LOG_PREFIX} ❌ ${message}`, ...args);
  }
}
